import Action from '../action';
import {ApiResult} from '../../../types/api-result';
import EventDispatcher from '../../event/event-dispatcher';
import Plugin from '../plugin';
import GeneralUtility from '../../utility/general-utility';

/**
 * Base action for any Lawyer API endpoints.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default abstract class ApiAction extends Action {
  /**
   * Event called right before the list is updated.
   *
   * @type {EventDispatcher<{ [key: string]: any }>}
   * @private
   */
  private _beforeUpdateEvent = new EventDispatcher<{ [key: string]: any }>();

  /**
   * Main endpoint to use for fetching data from the Lawyer API.
   *
   * @type string
   * @private
   */
  private _endpoint: string;

  /**
   * Contains the list result.
   *
   * @type {HTMLElement}
   * @private
   */
  private readonly _resultContainer: HTMLElement;

  /**
   * Contains a no-result message. May be omitted, if no no-result message is needed.
   *
   * @type {HTMLElement|null}
   * @private
   */
  private readonly _noResultContainer: HTMLElement = null;


  /**
   * Constructor.
   *
   * @param {Plugin} plugin Parent plugin
   * @param {HTMLElement} container Main action container
   * @param {string} endpoint Main endpoint to use for fetching data from the Lawyer API
   */
  protected constructor(plugin: Plugin, container: HTMLElement, endpoint: string) {
    super(plugin, container);
    this._endpoint = endpoint;
    this._resultContainer = container.querySelector('[data-container="result"]');
    this._noResultContainer = container.querySelector('[data-container="noResult"]');
  }


  /**
   * Gets the event that is called right before the action is updated.
   *
   * @returns {EventDispatcher<{ [key: string]: any }>}
   */
  get beforeUpdateEvent(): EventDispatcher<{ [key: string]: any }> {
    return this._beforeUpdateEvent;
  }


  /**
   * Gets the main endpoint.
   *
   * @returns {string}
   */
  get endpoint(): string {
    return this._endpoint;
  }


  /**
   * Sets the main endpoint.
   *
   * @param {string} endpoint
   * @returns {void}
   */
  set endpoint(endpoint: string) {
    this._endpoint = endpoint;
  }


  /**
   * Gets the result container.
   *
   * @returns {HTMLElement}
   */
  get resultContainer(): HTMLElement {
    return this._resultContainer;
  }


  /**
   * Gets the no-result container.
   *
   * @returns {HTMLElement|null}
   */
  get noResultContainer(): HTMLElement | null {
    return this._noResultContainer;
  }


  /**
   * Shows the no-result container, if available.
   *
   * @returns {void}
   * @protected
   */
  protected showNoResultContainer(): void {
    if (this.noResultContainer !== null) {
      this.noResultContainer.classList.remove('d-none');
    }
  }


  /**
   * Hides the no-result container, if available.
   *
   * @returns {void}
   * @protected
   */
  protected hideNoResultContainer(): void {
    if (this.noResultContainer !== null) {
      this.noResultContainer.classList.add('d-none');
    }
  }


  /**
   * Builds query request parameters to be sent to the API. This includes setting the correct language for the endpoint, adding common settings and also
   * adding settings from the given plugin settings.
   *
   * @param {any} [settings] Plugin settings to be added to the query (defaults to nothing)
   * @returns {any} Query parameters
   */
  protected createRequestParameters(settings: any = {}): any {
    const parameters: any = {
      'url': '',
      'settings': settings
    };

    // Add query parameter to select the correct language
    if (GeneralUtility.languageId > 0) {
      parameters.settings.language = GeneralUtility.language;
    }

    if (this.settings) {
      // Add common FlexForm settings of the plugins to the query parameter
      // Add number of items per page to the query parameter
      if (this.settings.rows !== undefined && this.settings.rows) {
        parameters.settings.rows = this.settings.rows;
      }

      // Add rootline page UID as a default filter
      if (this.settings.rootline !== undefined && this.settings.rootline) {
        parameters.settings.rootline = this.settings.rootline;
      }

      // If a fixed limit is present, use it as start and rows
      if (this.settings.limit !== undefined && this.settings.limit) {
        parameters.settings.start = 0;
        parameters.settings.rows = this.settings.limit;
      }
    }

    // Build URL to the API
    parameters.url = GeneralUtility.lawyerApiUrl;
    parameters.url += this.endpoint;

    // Convert settings object to URL query parameters
    if (settings !== null) {
      const queryParameters = GeneralUtility.convertObjectToQueryParameters(settings);
      if (queryParameters.length > 0) {
        parameters.url += '?' + queryParameters.join('&');
      }
    }

    return parameters;
  }


  /**
   * Updates the list.
   *
   * @param {any} settings Plugin parameters
   * @returns {void}
   * @protected
   */
  protected abstract update(settings: any): void;

  /**
   * Initializes the result.
   *
   * @param {ApiResult} result API result
   * @param {any} settings Plugin settings
   * @returns {void}
   * @protected
   */
  protected abstract initializeResult(result: ApiResult, settings: any): void;
}
